/**
 * A small confirmation dialog.
 *
 * Two flows need one: clearing a whole sketch, and the "clean up the walls you
 * just drew?" prompt that replaced the standalone Cleanup button (owner,
 * 2026-07-29). Both are moments where a stray tap costs the designer work, so
 * they get a real dialog rather than a native `confirm()`:
 *
 *  - `confirm()` cannot be styled, looks foreign on a tablet, and is blocked
 *    outright in some embedded contexts — a silently-skipped confirmation on a
 *    destructive action is the worst possible failure.
 *  - This one matches the app's other overlays, and its buttons are real touch
 *    targets rather than 20px system chrome.
 *
 * Returns a Promise that resolves true (confirmed) or false (cancelled,
 * dismissed by Escape, or by tapping outside). It never rejects — a dismissed
 * dialog is a "no", not an error.
 */

/** The dialog currently on screen, so two can never stack. */
let openDialog = null;

/**
 * @param {object} opts
 * @param {string} opts.title short, states the action
 * @param {string} [opts.body] the consequence, in plain words
 * @param {string} [opts.confirmLabel] defaults to "Confirm"
 * @param {string} [opts.cancelLabel] defaults to "Cancel"
 * @param {boolean} [opts.danger] styles the confirm button as destructive
 * @returns {Promise<boolean>}
 */
export function confirmAction({ title, body = '', confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false } = {}) {
  closeConfirm();
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'confirm-overlay';

    const card = document.createElement('div');
    card.className = 'confirm-card';
    card.setAttribute('role', 'alertdialog');
    card.setAttribute('aria-modal', 'true');
    card.setAttribute('aria-label', title ?? 'Confirm');

    const heading = document.createElement('h2');
    heading.className = 'confirm-card__title';
    heading.textContent = title ?? 'Are you sure?';
    card.append(heading);

    if (body) {
      const text = document.createElement('p');
      text.className = 'confirm-card__body';
      text.textContent = body;
      card.append(text);
    }

    const actions = document.createElement('div');
    actions.className = 'confirm-card__actions';

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'btn';
    cancel.textContent = cancelLabel;

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = `btn ${danger ? 'btn--danger' : 'btn--primary'}`;
    confirm.textContent = confirmLabel;

    actions.append(cancel, confirm);
    card.append(actions);
    overlay.append(card);
    document.body.append(overlay);

    const finish = (answer) => {
      cleanup();
      resolve(answer);
    };
    const onKey = (evt) => {
      if (evt.key === 'Escape') { evt.stopPropagation(); finish(false); }
      if (evt.key === 'Enter') { evt.stopPropagation(); finish(true); }
    };
    function cleanup() {
      document.removeEventListener('keydown', onKey, true);
      overlay.remove();
      if (openDialog === overlay) openDialog = null;
    }

    cancel.addEventListener('click', () => finish(false));
    confirm.addEventListener('click', () => finish(true));
    // Tapping the backdrop cancels; tapping the card itself must not.
    overlay.addEventListener('pointerdown', (evt) => { if (evt.target === overlay) finish(false); });
    document.addEventListener('keydown', onKey, true);

    openDialog = overlay;
    // Focus the SAFE choice, so a stray Enter or double-tap cannot confirm a
    // destructive action the designer never read.
    cancel.focus();
  });
}

/** Removes any open dialog without resolving it — used when tearing down. */
export function closeConfirm() {
  if (!openDialog) return;
  openDialog.remove();
  openDialog = null;
}
